/**
 * <anja-cart-pairs>: "Pairs well with" suggestions in the cart drawer.
 * - Suggestions: complementary + related recommendations, fetched in parallel and cached per product.
 * - Preselects the size already in the cart (exact label, else the size whose age range covers it).
 * - Adds through Horizon's CartLinesUpdateEvent flow so the drawer and count refresh; the card shows
 *   "Added" immediately and is removed, while the next suggestions are prefetched.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** @type {Map<string, Promise<string>>} product id → suggestions HTML (per page view) */
const cache = new Map();
const MAX = 6;
/** @type {string} */
let activeTab = 'recs';

/** @param {string} label @returns {[number, number] | null} */
function range(label) {
  const m = label.match(/(\d+)\D{0,4}?\s*[-–]\s*(\d+)/);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/** @param {string[]} values @param {string} preferred */
function bestSize(values, preferred) {
  if (!preferred) return null;
  const exact = values.find((v) => v.toLowerCase() === preferred.toLowerCase());
  if (exact) return exact;
  const r = range(preferred);
  if (!r) return null;
  const mid = (r[0] + r[1]) / 2;
  return values.find((v) => {
    const x = range(v);
    return x && mid >= x[0] && mid <= x[1];
  }) || null;
}

class AnjaCartPairs extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude', 'data-preferred-size'];

  /** @type {MutationObserver | undefined} */
  #observer;
  #busy = false;
  #pending = 0;

  connectedCallback() {
    this.#observer = new MutationObserver(() => {
      const list = this.querySelector('[data-anja-pairs-list]');
      if (list && !list.children.length) this.#schedule();
    });
    this.#observer.observe(this, { childList: true, subtree: true });
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#schedule();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  #schedule() {
    cancelAnimationFrame(this.#pending);
    this.#pending = requestAnimationFrame(() => this.#render());
  }

  /** Active tab survives the drawer re-render (module scope). */
  get #tab() {
    return this.querySelector(`[data-tab="${activeTab}"]`) ? activeTab : 'recs';
  }

  /** @param {string} key - 'recs:<productId>' or 'collection:<handle>' */
  #load(key) {
    if (!cache.has(key)) {
      const root = this.dataset.root || '/';
      /** @param {string} html */
      const parse = (html) =>
        Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.anja-pair')).map((el) => el.outerHTML);
      /** @param {string} url @returns {Promise<string[]>} */
      const get = (url) =>
        fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then(parse)
          .catch(() => []);
      let promise;
      if (key.startsWith('collection:')) {
        promise = get(`${root}collections/${encodeURIComponent(key.slice(11))}?section_id=anja-pairs-render`);
      } else {
        const id = key.slice(5);
        /** @param {string} intent */
        const rec = (intent) => get(`${root}recommendations/products?product_id=${id}&limit=8&intent=${intent}&section_id=anja-pairs-render`);
        promise = Promise.all([rec('complementary'), rec('related')]).then(([a, b]) => [...a, ...b]);
      }
      cache.set(
        key,
        promise.then((list) => {
          const seen = new Set();
          return list
            .filter((html) => {
              const m = html.match(/data-product-id="(\d+)"/);
              if (!m || seen.has(m[1])) return false;
              seen.add(m[1]);
              return true;
            })
            .join('');
        })
      );
    }
    return /** @type {Promise<string>} */ (cache.get(key));
  }

  async #render() {
    const id = this.dataset.productId;
    const list = this.querySelector('[data-anja-pairs-list]');
    if (!id || !list) return;
    const tab = this.#tab;
    this.querySelectorAll('[data-tab]').forEach((b) => b.setAttribute('aria-selected', String(/** @type {HTMLElement} */ (b).dataset.tab === tab)));
    const key = tab === 'recs' ? `recs:${id}` : tab;
    list.setAttribute('aria-busy', 'true');
    const html = await this.#load(key);
    if (id !== this.dataset.productId || tab !== this.#tab) return;

    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    let items = /** @type {HTMLElement[]} */ (Array.from(tpl.content.querySelectorAll('.anja-pair'))).filter(
      (el) => !exclude.has(el.dataset.productId || '')
    );
    const tabButton = /** @type {HTMLElement | null} */ (this.querySelector(`[data-tab="${tab}"]`));
    const near = Number(tabButton?.dataset.sortNear || 0);
    if (near > 0) {
      // Items that reach free delivery on their own first (cheapest first), then the rest (dearest first).
      /** @param {HTMLElement} el */
      const score = (el) => {
        const p = Number(el.dataset.price || 0);
        return p >= near ? p - near : 1e9 - p;
      };
      items.sort((a, b) => score(a) - score(b));
    }
    items = items.slice(0, MAX);
    items.forEach((card) => this.#prepare(card));
    this.#observer?.disconnect();
    list.replaceChildren(...items);
    list.removeAttribute('aria-busy');
    this.hidden = items.length === 0 && tab === 'recs';
    this.#observer?.observe(this, { childList: true, subtree: true });
  }

  /** Preselect the cart's size and resolve the variant. @param {HTMLElement} card */
  #prepare(card) {
    const group = card.querySelector('[data-size-option]');
    if (group) {
      const buttons = /** @type {HTMLButtonElement[]} */ (Array.from(group.querySelectorAll('[data-value]')));
      const pick = bestSize(buttons.map((b) => b.dataset.value || ''), this.dataset.preferredSize || '');
      const chosen = buttons.find((b) => b.dataset.value === pick);
      if (chosen) this.#selectSize(card, chosen);
    }
    this.#update(card);
  }

  /** @param {HTMLElement} card @param {HTMLElement} button */
  #selectSize(card, button) {
    card.querySelectorAll('[data-size-option] [data-value]').forEach((b) => b.setAttribute('aria-checked', String(b === button)));
    const row = button.parentElement;
    if (row) requestAnimationFrame(() => (row.scrollLeft += button.getBoundingClientRect().left - row.getBoundingClientRect().left - 4));
  }

  /** @param {HTMLElement} card */
  #variant(card) {
    /** @type {{id:number, options:string[], available:boolean, price:string}[]} */
    const variants = JSON.parse(card.querySelector('[data-anja-pair-variants]')?.textContent || '[]');
    const chosen = Array.from(card.querySelectorAll('[data-anja-pair-option]')).map((el) => {
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) return el.value;
      return /** @type {HTMLElement | null} */ (el.querySelector('[aria-checked="true"]'))?.dataset.value || null;
    });
    if (!chosen.length) return variants.find((v) => v.available) || null;
    if (chosen.some((v) => v === null)) return undefined;
    return variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;
  }

  /** @param {HTMLElement} card */
  #update(card) {
    const variant = this.#variant(card);
    const add = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-anja-pair-add]'));
    if (!add) return;
    // Mark sizes that are sold out for the chosen color.
    const variants = JSON.parse(card.querySelector('[data-anja-pair-variants]')?.textContent || '[]');
    const sizeGroup = card.querySelector('[data-size-option]');
    if (sizeGroup) {
      const sizeIndex = Number(sizeGroup.getAttribute('data-anja-pair-option'));
      const others = Array.from(card.querySelectorAll('select[data-anja-pair-option], input[data-anja-pair-option]')).map((el) => [
        Number(el.getAttribute('data-anja-pair-option')),
        /** @type {HTMLInputElement} */ (el).value,
      ]);
      sizeGroup.querySelectorAll('[data-value]').forEach((b) => {
        const ok = variants.some(
          (v) => v.available && v.options[sizeIndex] === /** @type {HTMLElement} */ (b).dataset.value && others.every(([i, val]) => v.options[i] === val)
        );
        b.classList.toggle('is-unavailable', !ok);
      });
    }
    if (variant === undefined) {
      add.disabled = false;
      add.dataset.state = 'choose';
      add.textContent = this.dataset.labelChoose || '';
      return;
    }
    add.dataset.state = variant && variant.available ? 'ready' : 'unavailable';
    add.disabled = !variant || !variant.available;
    add.textContent = variant && variant.available ? this.dataset.labelAdd || '' : this.dataset.labelSoldOut || '';
    const price = card.querySelector('[data-anja-pair-price]');
    if (variant && price && !price.querySelector('s')) price.textContent = variant.price;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const card = /** @type {HTMLElement} */ (event.target).closest?.('.anja-pair');
    if (card instanceof HTMLElement) this.#update(card);
  };

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tabButton = target.closest('[data-tab]');
    if (tabButton instanceof HTMLElement) {
      activeTab = tabButton.dataset.tab || 'recs';
      this.#render();
      return;
    }
    const card = target.closest('.anja-pair');
    if (!(card instanceof HTMLElement)) return;

    const size = target.closest('[data-size-option] [data-value]');
    if (size instanceof HTMLElement) {
      this.#selectSize(card, size);
      this.#update(card);
      return;
    }

    const button = target.closest('[data-anja-pair-add]');
    if (!(button instanceof HTMLButtonElement) || this.#busy) return;
    const variant = this.#variant(card);
    if (variant === undefined) {
      card.querySelector('[data-size-option]')?.classList.add('is-attention');
      setTimeout(() => card.querySelector('[data-size-option]')?.classList.remove('is-attention'), 900);
      return;
    }
    if (!variant || !variant.available) return;

    this.#busy = true;
    button.disabled = true;
    button.textContent = this.dataset.labelAdding || '';
    // Warm the next suggestions while the cart updates.
    this.#load(`recs:${card.dataset.productId || ''}`);

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: sections.join(',') }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Add failed');
      button.textContent = this.dataset.labelAdded || '✓';
      card.classList.add('is-added');
      try {
        window.Shopify?.analytics?.publish?.('anja_pair_added', { variant_id: variant.id, product_id: card.dataset.productId });
      } catch {}
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'anja-cart-pairs', itemCount: 1, sections: data.sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: String(/** @type {Error} */ (error)?.message || error),
          code: 'INVALID',
          detail: { description: String(/** @type {Error} */ (error)?.message || '') },
        })
      );
      button.disabled = false;
      button.textContent = this.dataset.labelAdd || '';
    } finally {
      this.#busy = false;
    }
  };
}

if (!customElements.get('anja-cart-pairs')) customElements.define('anja-cart-pairs', AnjaCartPairs);
